import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { LocalizedMessage } from 'intl/LocalizedComponents';
import _delay from 'lodash/delay';
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import PropTypes from 'prop-types';
// HOCs
import withConfirmDialog from 'hocs/WithConfirmDialog';
import withEnterKeyManager from 'hocs/WithEnterKeyManager';
import withFormManagement from 'hocs/WithFormManagement';
import withStateReinit from 'hocs/WithStateReinit';
import withTitle from 'hocs/WithTitle';
// Hooks
import useResponsive from 'hooks/useResponsive';
import usePrevious from 'hooks/usePrevious';
// Core components
import { ContainedButton, TextButton } from 'components/core/Button';
import Card from 'components/core/Card';
import TextField from 'components/core/TextField';
import Table from 'components/core/table/Table';
import TableHead from 'components/core/table/TableHead';
import TableRow from 'components/core/table/TableRow';
import TableCell from 'components/core/table/TableCell';
import Typography from 'components/core/Typography';
// Models
import { buildSuccess } from 'models/notification';
import mProduct from 'models/product';
import { serializeMe } from 'models/user';
import mAuth from 'models/auth';
// Custom
import {
  openDialog,
  getMe,
  getAlertings,
  updateMe,
  addNotification,
  changeLocale,
  cpmDeleteMyPaymentMethod,
} from 'actions';
import { meShape } from 'constants/PropTypesShapes';
import {
  buildFormErrors,
  completeFormErrors,
  meConstraints,
  cpmAddressConstraints,
} from 'utils/Forms';
import { isValidPhoneNumber, numberTo2DigitsString } from 'utils/Tools';
import PhoneNumberInputField from 'components/inputFields/PhoneNumberInputField';
import PasswordInputField from 'components/inputFields/PasswordInputField';
import LanguageSelector from 'components/inputFields/LanguageSelector';
import UserCallbacks from 'users/assets/main/UserCallbacks';
import UserAlertings from 'users/assets/main/UserAlertings';
import { DeleteIconButton } from 'components/buttons/IconButtons';
import CountryFieldSelector from 'components/inputFields/CountryFieldSelector';
import AppIcon from 'components/icons/AppIcon';
import ErrorsDisplayer from 'components/misc/ErrorsDisplayer';
import { CpmClientContext } from 'components/misc/Contexts';

// From product, get if alertings and callbacks are usable
const { withMeAlertings, withMeCallbacks, withMePaymentMethods } = mProduct.getConfig();

const SectionTitle = ({ iconId, messageId }) => (
  <div className="d-flex flex-row mb-3">
    <AppIcon id={iconId} className="mr-2" primary />
    <Typography type="cardTitle">
      <LocalizedMessage id={messageId} />
    </Typography>
  </div>
);
SectionTitle.propTypes = {
  iconId: PropTypes.string.isRequired,
  messageId: PropTypes.string.isRequired,
};

const Account = ({
  fields,
  me,
  locale,
  errors,
  onErrorsChange,
  onFieldsChange,
  onKeyPress,
  onResetForm,
  isResetDisabled,
  handleDisplayConfirm: dispatchDisplayConfirm,
  dispatchDeleteMyPaymentMethod,
  dispatchGetAlertings,
  dispatchGetMe,
  dispatchUpdateMe,
  dispatchAddNotification,
  dispatchChangeLocale,
  dispatchOpenDialog,
  paymentMethodDeleted,
}) => {
  /* ========================================================= */
  /* STATE                                                     */
  /* ========================================================= */
  const isMobile = useResponsive();
  const prevMeUpdated = usePrevious(me.update.updated);
  const prevPaymentMethodDeleted = usePrevious(paymentMethodDeleted);

  // If app is Flow client, we want to manage the display of "Book" button depending on the page customer is
  const cpmClientContext = React.useContext(CpmClientContext);

  /* ========================================================= */
  /* EFFECTS                                                   */
  /* ========================================================= */

  React.useEffect(() => {
    if (cpmClientContext) cpmClientContext.setShowBookButton(true);
  }, []);

  React.useEffect(function checkAlertsToDisplay() {
    if (withMeAlertings) {
      dispatchGetAlertings();
    }
  }, []);

  React.useEffect(function confirmPersonalInformationHasBeenUpdatedAndRefreshMe() {
    if ((prevMeUpdated === false && me.update.updated === true)) {
      dispatchGetMe();
      _delay(dispatchAddNotification, 400, buildSuccess('success.meUpdated'));
      if (fields.locale !== locale) {
        dispatchChangeLocale(fields.locale);
      }
    }
  }, [me.update.updated]);

  React.useEffect(function confirmPaymentMethodHasBeenDeletedAndRefreshMe() {
    if (prevPaymentMethodDeleted === false && paymentMethodDeleted === true) {
      dispatchGetMe();
      _delay(dispatchAddNotification, 400, buildSuccess('success.meUpdated'));
    }
  }, [paymentMethodDeleted]);

  /* ========================================================= */
  /* HANDLERS                                                  */
  /* ========================================================= */

  const handleDeletePaymentMethod = ({ provider, stripeDetails }) => {
    dispatchDisplayConfirm(
      { id: 'confirm.deleteMyPaymentMethod' },
      () => {
        dispatchDeleteMyPaymentMethod({
          id: stripeDetails.id,
          provider,
        });
      },
      { confirmLabel: 'confirm' },
    );
  };

  /* PERSONAL INFORMATION */
  /* ==================== */
  const PersonalInformation = <SectionTitle iconId="itemUserOutline" messageId="title.myPersonalInformation" />;

  /* First name */
  /* ========== */
  const FirstName = (
    <TextField
      label={<LocalizedMessage id="field.lastname" />}
      errorText={_get(errors, 'lastname')}
      value={fields.lastname}
      required
      onChange={(value) => onFieldsChange({ lastname: value })}
      onKeyPress={onKeyPress}
    />
  );

  /* Last name */
  /* ========== */
  const LastName = (
    <TextField
      label={<LocalizedMessage id="field.firstname" />}
      errorText={_get(errors, 'firstname')}
      value={fields.firstname}
      required
      onChange={(value) => onFieldsChange({ firstname: value })}
      onKeyPress={onKeyPress}
    />
  );

  /* Language */
  /* ======== */
  const Language = (
    <LanguageSelector
      errorText={_get(errors, 'locale')}
      value={fields.locale}
      required
      onChange={(newLocale) => onFieldsChange({ locale: newLocale })}
    />
  );

  /* Phone */
  /* ===== */
  const Phone = (
    <PhoneNumberInputField
      labelId="field.phoneNumber"
      value={fields.phone}
      errorText={_get(errors, 'phone')}
      onChange={(value) => onFieldsChange({ phone: value })}
      onKeyPress={onKeyPress}
    />
  );

  /* ====================================== */

  /* LOGIN CREDENTIALS */
  /* ================= */
  const LoginCredentials = <SectionTitle iconId="itemLogin" messageId="title.myLoginCredentials" />;

  /* Email */
  /* ===== */
  const EmailInput = (
    <TextField
      type="email"
      name="email"
      autoComplete="email"
      label={<LocalizedMessage id="field.email" />}
      errorText={_get(errors, 'email')}
      value={fields.email}
      required
      onChange={(value) => onFieldsChange({ email: value })}
      onKeyPress={onKeyPress}
    />
  );

  /* New password */
  /* ============ */
  const NewPassword = (
    <PasswordInputField
      autoComplete="new-password"
      label={<LocalizedMessage id="field.newPassword" />}
      errorText={_get(errors, 'password')}
      value={fields.newPassword}
      onChange={(value) => onFieldsChange({ newPassword: value })}
      onKeyPress={onKeyPress}
    />
  );

  /* New password confirmation */
  /* ======================== */
  const NewPasswordConfirmation = (
    <PasswordInputField
      autoComplete="new-password"
      label={<LocalizedMessage id="field.newPasswordConfirmation" />}
      errorText={_get(errors, 'passwordConfirmation')}
      value={fields.newPasswordConfirmation}
      onChange={(value) => onFieldsChange({ newPasswordConfirmation: value })}
      onKeyPress={onKeyPress}
    />
  );

  /* ====================================== */

  /* BILLING DETAILS */
  /* =============== */
  const BillingDetails = <SectionTitle iconId="invoice" messageId="title.myInvoiceInformation" />;

  /* Company */
  /* ======= */
  const Company = (
    <TextField
      label={<LocalizedMessage id="field.company" />}
      errorText={_get(errors, 'companyName')}
      value={fields.companyName}
      onChange={(value) => onFieldsChange({ companyName: value })}
      onKeyPress={onKeyPress}
    />
  );

  /* National Identifier */
  /* ================== */
  const NationalIdentifier = (
    <TextField
      label={<LocalizedMessage id="field.nationalIdentifier" />}
      value={fields.nationalIdentifier}
      onChange={(value) => onFieldsChange({ nationalIdentifier: value })}
      onKeyPress={onKeyPress}
    />
  );

  /* Address */
  /* ======= */
  const Address = (
    <TextField
      label={<LocalizedMessage id="field.address" />}
      errorText={_get(errors, 'street')}
      value={fields.street}
      onChange={(value) => onFieldsChange({ street: value })}
      onKeyPress={onKeyPress}
    />
  );

  /* Address additional */
  /* ================== */
  const AddressAdditional = (
    <TextField
      name="extra"
      label={<LocalizedMessage id="field.addressAdditional" />}
      value={fields.extra}
      onChange={(value) => onFieldsChange({ extra: value })}
    />
  );

  /* Zip code */
  /* ======== */
  const ZipCode = (
    <TextField
      name="zipCode"
      label={<LocalizedMessage id="field.zipcode" />}
      errorText={_get(errors, 'zipCode')}
      value={fields.zipCode}
      onChange={(value) => onFieldsChange({ zipCode: value })}
    />
  );

  /* City */
  /* ==== */
  const City = (
    <TextField
      name="city"
      label={<LocalizedMessage id="field.city" />}
      errorText={_get(errors, 'city')}
      value={fields.city}
      onChange={(value) => onFieldsChange({ city: value })}
    />
  );

  /* Country */
  /* ======= */
  const Country = (
    <CountryFieldSelector
      countriesScope="known"
      value={fields.country}
      errorText={_get(errors, 'country')}
      onChange={(value) => onFieldsChange({ country: value })}
    />
  );

  /* ====================================== */

  /* PAYMENT METHODS */
  /* =============== */

  const PaymentMethods = <SectionTitle iconId="btnPayment" messageId="title.myPaymentMethods" />;

  const Alertings = withMeAlertings && (
    <div className="pt-4">
      <div className="row" style={{ padding: '16px' }}>
        <Typography type="cardTitle">
          <LocalizedMessage id="title.myAlertings" />
        </Typography>
      </div>

      <UserAlertings
        userAlertings={fields.userAlertings}
        onChange={(value) => onFieldsChange({ userAlertings: value })}
      />
    </div>
  );

  const Callbacks = withMeCallbacks && (
    <div className="pt-4">
      <div className="row" style={{ padding: '16px' }}>
        <Typography type="cardTitle">
          <LocalizedMessage id="title.callbacks" />
        </Typography>
      </div>
      <UserCallbacks
        cbVersion={fields.cbVersion}
        cbUri={fields.cbUri}
        cbSubscriptions={fields.cbSubscriptions}
        errors={_get(errors, 'callback')}
        onChange={(value) => onFieldsChange({
          cbVersion: value.cbVersion,
          cbUri: value.cbUri,
          cbSubscriptions: value.cbSubscriptions,
        })}
        onKeyPress={onKeyPress}
      />
    </div>
  );

  /* ====================================== */

  /* Save Modifications */
  /* ================= */

  const saveModifications = (e) => {
    if (e) e.preventDefault();

    let clientFormErrors = buildFormErrors({
      locale: fields.locale,
      lastname: fields.lastname,
      firstname: fields.firstname,
      email: fields.email,
    }, meConstraints);

    const phoneIsInvalid = fields.phone && !isValidPhoneNumber(fields.phone);
    if (phoneIsInvalid) clientFormErrors = completeFormErrors(clientFormErrors, 'phone', ['isInvalid']);

    const newPasswordsDontMatch = fields.newPassword && fields.newPassword !== fields.newPasswordConfirmation;
    if (newPasswordsDontMatch) clientFormErrors = completeFormErrors(clientFormErrors, 'passwordConfirmation', ['notTheSame']);

    // Check callbacks
    let withCallbacks = false;
    const thereisNewPaymentMethod = !_isEmpty(fields.cbVersion) || !_isEmpty(fields.cbUri) || !_isEmpty(fields.cbSubscriptions);
    if (thereisNewPaymentMethod) {
      withCallbacks = true;
      const missingCbVersion = _isEmpty(fields.cbVersion);
      const missingCbUri = _isEmpty(fields.cbUri);

      if (missingCbVersion) clientFormErrors = completeFormErrors(clientFormErrors, 'callback.version', ['cantBeBlank']);
      if (missingCbUri) clientFormErrors = completeFormErrors(clientFormErrors, 'callback.uri', ['cantBeBlank']);
    }

    if (clientFormErrors) {
      onErrorsChange(clientFormErrors);
      return;
    }

    const thereIsNewAddressInformation = fields.city || fields.country || fields.extra || fields.street || fields.zipCode;
    if (thereIsNewAddressInformation) {
      clientFormErrors = buildFormErrors(
        {
          city: fields.city,
          country: fields.country,
          extra: fields.extra,
          street: fields.street,
          zipCode: fields.zipCode,
        },
        cpmAddressConstraints,
      );
    }

    if (clientFormErrors) {
      onErrorsChange(clientFormErrors);
      return;
    }

    if (!clientFormErrors) {
      // Build callback
      let callback = null;
      if (withCallbacks) {
        callback = {
          version: fields.cbVersion,
          uri: fields.cbUri,
          subscriptions: fields.cbSubscriptions,
        };
      }

      const meData = {
        locale: fields.locale,
        firstname: fields.firstname,
        lastname: fields.lastname,
        email: fields.email,
        phone: fields.phone,
        callback: withMeCallbacks ? callback : null,
        userAlertings: withMeAlertings ? fields.userAlertings : null,
        companyName: fields.companyName,
        invoiceAddress: fields.city
          ? {
            city: fields.city,
            country: fields.country,
            extra: fields.extra,
            street: fields.street,
            zipCode: fields.zipCode,
          }
          : null,
        nationalIdentifier: fields.nationalIdentifier,
      };

      const emailHasChanged = fields.email !== me.data.email;
      const needPasswordConfirmation = Boolean(fields.newPassword) || emailHasChanged;

      if (!needPasswordConfirmation) {
        dispatchUpdateMe(serializeMe(meData));
      }

      if (needPasswordConfirmation) {
        dispatchOpenDialog({
          title: {
            id: 'title.passwordConfirmation',
          },
          actions: [
            { type: 'cancel' },
            { type: 'validate' },
          ],
          component: {
            type: 'PASSWORD_CONFIRMATION',
            props: {
              onValidate: (currentPassword) => {
                dispatchUpdateMe(
                  serializeMe({
                    ...meData,
                    currentPassword,
                    password: fields.newPassword,
                    passwordConfirmation: fields.newPasswordConfirmation,
                  }),
                );
              },
            },
          },
        });
      }
    }
  };

  const Reset = onResetForm ? (
    <TextButton
      label={<LocalizedMessage id="button.reset" />}
      onClick={onResetForm}
      disabled={isResetDisabled}
    />
  ) : null;
  const SaveModifications = <ContainedButton label={<LocalizedMessage id="button.saveModif" />} onClick={saveModifications} />;

  const Buttons = (
    <div className="mt-3 d-flex flex-wrap align-items-center" style={{ gap: '10px', justifyContent: isMobile ? 'center' : 'flex-end' }}>
      {Reset}
      {SaveModifications}
    </div>
  );

  /* ====================================== */

  /* Payment Methods */
  /* =============== */
  const paymentMethodsColumns = [
    {
      key: 'brand',
      labelId: 'field.type',
      field: 'brand',
      sortable: false,
    },
    {
      key: 'expDate',
      labelId: 'field.expirationDate',
      field: 'expirationDate',
      sortable: false,
    },
    {
      key: 'last4',
      labelId: 'field.last4Digits',
      field: 'last4',
      sortable: false,
    },
  ];

  const paymentMethods = (me && me.data && me.data.paymentMethods) ? me.data.paymentMethods : [];

  const PaymentMethodRow = ({
    brand,
    expMonth,
    expYear,
    last4,
    provider,
    stripeDetails,
  }) => (
    <TableRow key={`${expMonth}-${expYear}-${last4}`}>
      <TableCell className="text-capitalize">{brand}</TableCell>
      <TableCell>{`${numberTo2DigitsString(expMonth)}/${expYear}`}</TableCell>
      <TableCell>{last4}</TableCell>
      <TableCell noPadding className="table-actions-1">
        <DeleteIconButton onClick={() => handleDeletePaymentMethod({ provider, stripeDetails })} />
      </TableCell>
    </TableRow>
  );
  PaymentMethodRow.propTypes = {
    brand: PropTypes.string.isRequired,
    expMonth: PropTypes.number.isRequired,
    expYear: PropTypes.number.isRequired,
    last4: PropTypes.string.isRequired,
    provider: PropTypes.string.isRequired,
    stripeDetails: PropTypes.object.isRequired,
  };

  const PaymentMethodsCard = withMePaymentMethods ? (
    <Table
      hasElements
      head={<TableHead columns={paymentMethodsColumns} adjustForActions nbActions={1} />}
      withFooter={false}
    >
      {paymentMethods.map(PaymentMethodRow)}
    </Table>
  ) : null;

  /* ========================================================= */
  /* UI                                                        */
  /* ========================================================= */

  const SECTIONS_ROW_GAP = '70px';

  const INPUTS_COLUMN_GAP = '30px';
  const INPUTS_ROW_GAP = '0px';
  const INPUT_MIN_WIDTH = '190px';
  const INPUTS_MAX_COLUMNS = 2;
  const INPUT_MAX_WIDTH = '780px';
  const INPUTS_GRID_TEMPLATE_COLUMNS = `repeat(auto-fill, minmax(max(${INPUT_MIN_WIDTH}, calc((100% - ${INPUTS_COLUMN_GAP}) / ${INPUTS_MAX_COLUMNS})), 1fr))`;

  const twoColumns = {
    display: 'grid',
    columnGap: INPUTS_COLUMN_GAP,
    rowGap: INPUTS_ROW_GAP,
    gridTemplateColumns: INPUTS_GRID_TEMPLATE_COLUMNS,
    maxWidth: INPUT_MAX_WIDTH,
  };

  return (
    <React.Fragment>

      <ErrorsDisplayer errors={errors} additionalError={_get(errors, 'currentPassword')} />

      {/* Main form */}
      <form autoComplete="off">
        <Card>
          <div className="d-flex flex-column py-3" style={{ rowGap: SECTIONS_ROW_GAP }}>
            <section>
              {PersonalInformation}
              <div style={twoColumns}>
                {FirstName}
                {LastName}
                {Language}
                {Phone}
              </div>
            </section>

            <section>
              {LoginCredentials}
              <div style={twoColumns}>
                {EmailInput}
                <div />
              </div>
              <div style={twoColumns}>
                {NewPassword}
                {NewPasswordConfirmation}
              </div>
            </section>

            {withMePaymentMethods ? (
              <section>
                {BillingDetails}
                <div style={twoColumns}>
                  {Company}
                  {NationalIdentifier}
                  {Address}
                  {AddressAdditional}
                  {ZipCode}
                  {City}
                  {Country}
                </div>
              </section>
            ) : null}

            {Alertings}
            {Callbacks}

            {Buttons}
          </div>
        </Card>
      </form>

      {/* List of payment methods */}
      {withMePaymentMethods ? (
        <div className="mt-4">
          <Card
            title={PaymentMethods}
            contentOptions={{ expandable: true, expanded: true }}
            defaultHeader
          >
            {PaymentMethodsCard}
          </Card>
        </div>
      ) : null}
    </React.Fragment>
  );
};

Account.propTypes = {
  me: meShape.isRequired,
  locale: PropTypes.string.isRequired,
  fields: PropTypes.object.isRequired,
  errors: PropTypes.object,
  onFieldsChange: PropTypes.func.isRequired,
  onErrorsChange: PropTypes.func.isRequired,
  onKeyPress: PropTypes.func.isRequired,
  onResetForm: PropTypes.func.isRequired,
  isResetDisabled: PropTypes.bool.isRequired,
  paymentMethodDeleted: PropTypes.bool.isRequired,
  dispatchGetMe: PropTypes.func.isRequired,
  dispatchGetAlertings: PropTypes.func.isRequired,
  dispatchUpdateMe: PropTypes.func.isRequired,
  dispatchAddNotification: PropTypes.func.isRequired,
  dispatchChangeLocale: PropTypes.func.isRequired,
  handleDisplayConfirm: PropTypes.func.isRequired,
  dispatchDeleteMyPaymentMethod: PropTypes.func.isRequired,
  dispatchOpenDialog: PropTypes.func.isRequired,
};

Account.defaultProps = {
  errors: null,
};

const mapStateToProps = (state) => ({
  me: state.me,
  paymentMethodDeleted: state.me.deleteMyPaymentMethod.deleted,
  locale: state.intl.locale,
});

const mapDispatchToProps = (dispatch) => ({
  dispatchGetMe: () => {
    dispatch(getMe());
  },
  dispatchGetAlertings: () => {
    dispatch(getAlertings());
  },
  dispatchUpdateMe: (data) => {
    dispatch(updateMe(data));
  },
  dispatchAddNotification: (notification) => {
    dispatch(addNotification(notification));
  },
  dispatchChangeLocale: (locale) => {
    dispatch(changeLocale(locale));
  },
  dispatchDeleteMyPaymentMethod: (data) => {
    dispatch(cpmDeleteMyPaymentMethod(mAuth.mutDeleteMyPaymentMethod, data));
  },
  dispatchOpenDialog: (props) => {
    dispatch(openDialog(props));
  },
});

const mapFieldsToProps = {
  locale: {
    resourceKey: 'locale',
    defaultValue: null,
  },
  firstname: {
    resourceKey: 'firstname',
    defaultValue: '',
  },
  lastname: {
    resourceKey: 'lastname',
    defaultValue: '',
  },
  email: {
    resourceKey: 'email',
    defaultValue: '',
  },
  newPassword: {
    defaultValue: '',
  },
  newPasswordConfirmation: {
    defaultValue: '',
  },
  phone: {
    resourceKey: 'phone',
    defaultValue: '',
  },
  cbVersion: {
    resourceKey: 'callback.version',
    defaultValue: '',
  },
  cbUri: {
    resourceKey: 'callback.uri',
    defaultValue: '',
  },
  cbSubscriptions: {
    resourceKey: 'callback.subscriptions',
    defaultValue: [],
  },
  userAlertings: {
    resourceKey: 'user_alertings',
    defaultValue: [],
  },
  companyName: {
    resourceKey: 'companyName',
    defaultValue: '',
  },
  city: {
    resourceKey: 'invoiceAddress.city',
    defaultValue: '',
  },
  country: {
    resourceKey: 'invoiceAddress.country',
    defaultValue: '',
  },
  extra: {
    resourceKey: 'invoiceAddress.extra',
    defaultValue: '',
  },
  street: {
    resourceKey: 'invoiceAddress.street',
    defaultValue: '',
  },
  zipCode: {
    resourceKey: 'invoiceAddress.zipCode',
    defaultValue: '',
  },
  nationalIdentifier: {
    resourceKey: 'nationalIdentifier',
    defaultValue: '',
  },
};

const mapOwnPropsToResources = (ownProps) => ({
  resourceData: ownProps.me.data,
  operationError: ownProps.me.update.error,
});

module.exports = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withFormManagement(mapFieldsToProps, mapOwnPropsToResources),
  withStateReinit(['updateMe']),
  withEnterKeyManager,
  withConfirmDialog,
  withTitle('pageTitle.myAccount'),
)(Account);



// WEBPACK FOOTER //
// ./routes/account/components/Account.js